import { Section } from 'components/Shared/Layout';
import connect from 'components/Shared/connect';
import { EditorialLink } from 'models/EditorialLink';
import { ColumnLayout, ColumnCount } from 'components/Shared/Layout/columnLayout';
import styles from './styles.scss';
import Image from 'components/Shared/Image';
import cn from 'classnames';
import { IconArrow } from 'components/Shared/Icon';
import { resource as rm, NO_ELEMENT } from 'utils';

const IMAGE_WIDTH = 100;
const IMAGE_HEIGHT = 100;

interface EditorialProps {
  editorials: EditorialLink[];
  title: string;
  seeAllEditorials: string;
  readEditorial: string;
  type: EditorialType;
  url: string;
}

type EditorialType = 'Article' | 'Guide';

const Editorials = ({
  editorials,
  title,
  seeAllEditorials,
  readEditorial,
  type,
  url
}: EditorialProps) => (
  <div
    className={cn(styles.editorial, {
      [styles.article]: type === 'Article',
      [styles.guide]: type === 'Guide'
    })}
  >
    <div className={styles.editorialsHeading}>
      <h3>{title}</h3>
      <a href={url}>{seeAllEditorials}</a>
    </div>
    <ColumnLayout columns={ColumnCount.Two} gutters>
      {editorials.map(editorial => (
        <div key={editorial.url}>
          <div className={styles.editorialInfo}>
            <div className={styles.imageWrapper}>
              <Image
                paddingBottom
                {...editorial.image}
                width={IMAGE_WIDTH}
                height={IMAGE_HEIGHT}
                alt={editorial.title}
              />
            </div>
            <div className={styles.editorialInfoText}>
              {type === 'Guide' && <p>{editorial.type}</p>}
              <h4>{editorial.title}</h4>
              {type === 'Guide' && (
                <a href={editorial.url}>
                  {readEditorial}
                  <IconArrow size="tiny" />
                </a>
              )}
            </div>
          </div>
          {type === 'Article' && (
            <div className={styles.articleIntro}>
              <p>{editorial.introduction}</p>
              <a href={editorial.url}>
                {readEditorial}
                <IconArrow size="tiny" />
              </a>
            </div>
          )}
        </div>
      ))}
    </ColumnLayout>
  </div>
);

interface OwnProps {
  articles?: EditorialLink[];
  guides?: EditorialLink[];
}

interface MergedProps extends OwnProps {
  articlesHeader: string;
  guidesHeader: string;
  articlesUrl: string;
  guidesUrl: string;
  gotoArticlesText: string;
  gotoGuidesText: string;
  readArticleText: string;
  readGuide: string;
}

const _ArticlesAndGuides = ({
  articles,
  guides,
  articlesHeader,
  guidesHeader,
  articlesUrl,
  guidesUrl,
  gotoArticlesText,
  gotoGuidesText,
  readArticleText,
  readGuide
}: MergedProps) =>
  // Checks inlined for TypeScript type inference
  (articles && articles.length > 0) || (guides && guides.length > 0) ? (
    <Section className={styles.articlesAndGuides} appearance="primary" shadow>
      <ColumnLayout gutters className={styles.articlesAndGuidsLayout} columns={ColumnCount.Two}>
        {articles && articles.length > 0 && (
          <Editorials
            editorials={articles}
            title={articlesHeader}
            seeAllEditorials={gotoArticlesText}
            readEditorial={readArticleText}
            url={articlesUrl}
            type="Article"
          />
        )}
        {guides && guides.length > 0 && (
          <Editorials
            editorials={guides}
            title={guidesHeader}
            seeAllEditorials={gotoGuidesText}
            readEditorial={readGuide}
            type="Guide"
            url={guidesUrl}
          />
        )}
      </ColumnLayout>
    </Section>
  ) : (
    NO_ELEMENT
  );

export const ArticlesAndGuides = connect<MergedProps, OwnProps>(({ appShell }) => ({
  articlesHeader: rm(appShell, 'reportage_recommended_stories_header'),
  guidesHeader: rm(appShell, 'reportage_recommended_articles_header'),
  articlesUrl: rm(appShell, 'reportage_see_all_reportage_link'),
  guidesUrl: rm(appShell, 'reportage_see_all_guides_link'),
  gotoArticlesText: rm(appShell, 'reportage_see_all_reportage_link_text'),
  gotoGuidesText: rm(appShell, 'reportage_see_all_guides_link_text'),
  readArticleText: rm(appShell, 'reportage_read_reportage_link_text'),
  readGuide: rm(appShell, 'reportage_read_guide_link_text')
}))(_ArticlesAndGuides);
